import React, { useState, useEffect, useCallback } from 'react';
import { toast } from 'react-toastify';
import api from '../../services/api';
import { confirmDelete } from '../../utils/confirm';

function SessionModal({ tenantId, session, academicYears, onClose, onSaved }) {
  const isEdit = !!session;
  const [form, setForm] = useState({
    academicYearId: session?.AcademicYearID || '',
    sessionName: session?.SessionName || '',
    code: session?.Code || '',
    startDate: session?.StartDate ? session.StartDate.substring(0, 10) : '',
    endDate: session?.EndDate ? session.EndDate.substring(0, 10) : '',
    sequenceNo: session?.SequenceNo != null ? session.SequenceNo : '',
    isActive: session ? session.IsActive : true,
  });
  const [saving, setSaving] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.academicYearId) {
      toast.error('Please select an Academic Year');
      return;
    }
    if (!form.sessionName.trim()) {
      toast.error('Academic Session Name is required');
      return;
    }

    setSaving(true);
    try {
      const payload = {
        tenantId,
        academicYearId: parseInt(form.academicYearId),
        sessionName: form.sessionName.trim(),
        code: form.code.trim() || null,
        startDate: form.startDate || null,
        endDate: form.endDate || null,
        sequenceNo: form.sequenceNo !== '' ? parseFloat(form.sequenceNo) : null,
        isActive: form.isActive,
      };

      if (isEdit) {
        await api.put(`/academic/sessions/${session.AcademicSessionID}`, payload);
        toast.success('Academic Session updated');
      } else {
        await api.post('/academic/sessions', payload);
        toast.success('Academic Session created');
      }
      onSaved();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content" onClick={(e) => e.stopPropagation()}>
        <h2>{isEdit ? 'Edit Academic Session' : 'Add Academic Session'}</h2>
        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label className="required-label">Academic Year</label>
            <select
              className="form-select"
              value={form.academicYearId}
              onChange={(e) => setForm({ ...form, academicYearId: e.target.value })}
            >
              <option value="">-- Select Academic Year --</option>
              {academicYears.map((y) => (
                <option key={y.AcademicYearID} value={y.AcademicYearID}>{y.YearName}</option>
              ))}
            </select>
          </div>
          <div className="form-group">
            <label className="required-label">Academic Session Name</label>
            <input
              type="text"
              placeholder="e.g., Semester 1, Term 1"
              value={form.sessionName}
              onChange={(e) => setForm({ ...form, sessionName: e.target.value })}
              autoFocus
            />
          </div>
          <div className="form-row">
            <div className="form-group">
              <label>Code</label>
              <input
                type="text"
                placeholder="e.g., SEM1"
                value={form.code}
                onChange={(e) => setForm({ ...form, code: e.target.value })}
              />
            </div>
            <div className="form-group">
              <label>Sequence No</label>
              <input
                type="number"
                step="0.1"
                placeholder="e.g., 1 or 1.2"
                value={form.sequenceNo}
                onChange={(e) => setForm({ ...form, sequenceNo: e.target.value })}
              />
            </div>
          </div>
          <div className="form-row">
            <div className="form-group">
              <label>Start Date</label>
              <input
                type="date"
                value={form.startDate}
                onChange={(e) => setForm({ ...form, startDate: e.target.value })}
              />
            </div>
            <div className="form-group">
              <label>End Date</label>
              <input
                type="date"
                value={form.endDate}
                onChange={(e) => setForm({ ...form, endDate: e.target.value })}
              />
            </div>
          </div>
          <div className="form-group">
            <label className="checkbox-inline">
              <input
                type="checkbox"
                checked={form.isActive}
                onChange={(e) => setForm({ ...form, isActive: e.target.checked })}
              />
              Is Active
            </label>
          </div>
          <div className="form-actions">
            <button type="button" className="btn btn-cancel-text" onClick={onClose}>Cancel</button>
            <button type="submit" className="btn btn-save" disabled={saving}>
              {saving ? 'Saving...' : isEdit ? 'Update' : 'Create'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function AcademicSessionsTab({ tenantId }) {
  const [sessions, setSessions] = useState([]);
  const [academicYears, setAcademicYears] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editSession, setEditSession] = useState(null);
  const [filterYear, setFilterYear] = useState('');

  const loadData = useCallback(async () => {
    if (!tenantId) return;
    try {
      setLoading(true);
      const [sessRes, yearRes] = await Promise.all([
        api.get(`/academic/sessions/${tenantId}`),
        api.get(`/academic/years/${tenantId}`),
      ]);
      if (sessRes.data.success) setSessions(sessRes.data.data);
      if (yearRes.data.success) setAcademicYears(yearRes.data.data);
    } catch {
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  }, [tenantId]);

  useEffect(() => { loadData(); }, [loadData]);

  const handleDelete = async (sessionId) => {
    if (!(await confirmDelete('Academic Session'))) return;
    try {
      await api.delete(`/academic/sessions/${sessionId}`);
      toast.success('Academic Session deleted');
      loadData();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to delete');
    }
  };

  const formatDate = (d) => {
    if (!d) return '-';
    return new Date(d).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
  };

  const filteredSessions = filterYear
    ? sessions.filter((s) => s.AcademicYearID === parseInt(filterYear))
    : sessions;

  return (
    <>
      <div className="page-card-header" style={{ margin: 0, paddingBottom: 16, borderBottom: '1px solid #e0e0e0' }}>
        <h3>Academic Sessions</h3>
        <button
          className="btn btn-add"
          onClick={() => { setEditSession(null); setShowModal(true); }}
          disabled={academicYears.length === 0}
          title={academicYears.length === 0 ? 'Create an Academic Year first' : ''}
        >
          + Add Session
        </button>
      </div>

      {/* Filter bar */}
      {academicYears.length > 0 && (
        <div className="user-filters" style={{ marginTop: 16 }}>
          <select
            className="filter-select"
            value={filterYear}
            onChange={(e) => setFilterYear(e.target.value)}
          >
            <option value="">All Academic Years</option>
            {academicYears.map((y) => (
              <option key={y.AcademicYearID} value={y.AcademicYearID}>{y.YearName}</option>
            ))}
          </select>
        </div>
      )}

      {loading ? (
        <div style={{ textAlign: 'center', padding: 40, color: '#999' }}>Loading...</div>
      ) : academicYears.length === 0 ? (
        <div style={{ textAlign: 'center', padding: 40, color: '#999' }}>
          Please create an Academic Year first before adding sessions.
        </div>
      ) : filteredSessions.length === 0 ? (
        <div style={{ textAlign: 'center', padding: 40, color: '#999' }}>
          No academic sessions found. Click "Add Session" to create one.
        </div>
      ) : (
        <table className="data-table" style={{ marginTop: 16 }}>
          <thead>
            <tr>
              <th>Seq</th>
              <th>Session Name</th>
              <th>Academic Year</th>
              <th>Code</th>
              <th>Start Date</th>
              <th>End Date</th>
              <th>Status</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {filteredSessions.map((s) => (
              <tr key={s.AcademicSessionID}>
                <td style={{ color: '#616161', fontWeight: 600, width: 60 }}>{s.SequenceNo ?? '-'}</td>
                <td>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <span style={{ fontSize: 18 }}>📋</span>
                    <strong>{s.SessionName}</strong>
                  </div>
                </td>
                <td>
                  <span className="badge badge-provider">{s.YearName}</span>
                </td>
                <td style={{ color: '#616161' }}>{s.Code || '-'}</td>
                <td>{formatDate(s.StartDate)}</td>
                <td>{formatDate(s.EndDate)}</td>
                <td>
                  <span className={`badge ${s.IsActive ? 'badge-active' : 'badge-inactive'}`}>
                    {s.IsActive ? 'Active' : 'Inactive'}
                  </span>
                </td>
                <td>
                  <div className="action-buttons">
                    <button className="action-btn action-edit" title="Edit" onClick={() => { setEditSession(s); setShowModal(true); }}>✏</button>
                    <button className="action-btn action-delete" title="Delete" onClick={() => handleDelete(s.AcademicSessionID)}>🗑</button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {showModal && (
        <SessionModal
          tenantId={tenantId}
          session={editSession}
          academicYears={academicYears}
          onClose={() => { setShowModal(false); setEditSession(null); }}
          onSaved={() => { setShowModal(false); setEditSession(null); loadData(); }}
        />
      )}
    </>
  );
}

export default AcademicSessionsTab;
